import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { runWithAuditReason } from '../../../api/client';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { usePositions, useSavePosition, useDeletePosition, errMessage, type Position } from './systemMasters';

interface Props { onHome?: () => void; }

/** Settings ▸ System Management ▸ Position — CRUD over dbo.Position (Name + Sort + Division-based flag). */
export function PositionPage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = usePositions();
  const loadMs = useFetchMs(isFetching);
  const save = useSavePosition();
  const del = useDeletePosition();

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [editing, setEditing] = useState<Position | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [sort, setSort] = useState('');
  const [err, setErr] = useState('');
  // Inline row-edit (1st pencil) — Position + Sort in place. IsDivisionBased is
  // hidden in LIMS (VisibleInDetailView/ListView false) → preserved, never shown.
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [dName, setDName] = useState('');
  const [dSort, setDSort] = useState('');
  const [savingInline, setSavingInline] = useState(false);
  // Child forms open maximized by default (LIMS parity), filling the content area
  // (the grid card region) — restore returns to the normal centred dialog.
  const [maxed, setMaxed] = useState(false);  // open RIGHT-SIZED (not full-screen); user can maximize via the header button
  const cardRef = useRef<HTMLDivElement>(null);
  const [maxRect, setMaxRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!maxed) { setMaxRect(null); return; }
    const measure = () => {
      const r = cardRef.current?.getBoundingClientRect();
      if (r) setMaxRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maxed]);
  const maxStyle = maxed && maxRect
    ? { position: 'fixed' as const, top: maxRect.top, left: maxRect.left, width: maxRect.width,
        height: maxRect.height, maxWidth: 'none' as const, maxHeight: 'none' as const, margin: 0 }
    : undefined;

  const openNew = () => { setEditing(null); setName(''); setSort(''); setErr(''); setMaxed(false); };
  const openEdit = (r: Position) => { setEditing(r); setName(r.positionName ?? ''); setSort(r.sort != null ? String(r.sort) : ''); setErr(''); setMaxed(false); };
  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one position to edit.'); return; }
    const r = rows.find((x) => x.id === String([...selectedIds][0])); if (r) openEdit(r);
  };
  const close = () => setEditing(undefined);

  const startInline = (r: Position) => { setInlineId(r.id); setDName(r.positionName ?? ''); setDSort(r.sort != null ? String(r.sort) : ''); };
  const cancelInline = () => { setInlineId(null); setDName(''); setDSort(''); };
  const saveInline = async (r: Position) => {
    if (!dName.trim()) { notify.warning("'Position' must not be empty"); return; }
    // Reason-for-change gate (standing rule 11) — only when ReasonRequired is ON.
    const reason = await gateReason('Position', 'edit');
    if (reason === null) return;
    setSavingInline(true);
    try {
      await runWithAuditReason(reason, () =>
        save.mutateAsync({ id: r.id, positionName: dName.trim(), sort: dSort.trim() === '' ? null : Number(dSort), isDivisionBased: r.isDivisionBased }));
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      cancelInline();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingInline(false); }
  };

  const submit = async () => {
    if (!name.trim()) { setErr("'Position' must not be empty"); return; }
    setErr('');
    const sortNum = sort.trim() === '' ? null : Number(sort);
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (editing?.id) {
      const r = await gateReason('Position', 'edit');
      if (r === null) return;
      reason = r;
    }
    await runSave({
      save: () => runWithAuditReason(reason, () => save.mutateAsync({ id: editing?.id, positionName: name.trim(), sort: sortNum, isDivisionBased: editing?.isDivisionBased ?? true }).then(() => {})),
      successMessage: 'Saved successfully.', onSaved: () => close(), onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one position to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} position${selectedIds.size === 1 ? '' : 's'}?` })) return;
    const reason = await gateReason('Position', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => (r.positionName ?? '').toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  const columns: AppColumn<Position>[] = [
    {
      field: 'inlineedit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) =>
        inlineId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingInline} onClick={() => void saveInline(r)}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingInline} onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit" onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-edit" /></button>
        ),
    },
    {
      field: 'editopen', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => inlineId === r.id ? null : (
        <button className="pts-icon-btn edit" title="Open full edit screen" onClick={(e) => { e.stopPropagation(); openEdit(r); }}><i className="ti ti-external-link" /></button>
      ),
    },
    {
      field: 'positionName', header: 'Position', minWidth: 260, text: (r) => r.positionName ?? '',
      compare: (a, b) => (a.positionName ?? '').localeCompare(b.positionName ?? '', undefined, { sensitivity: 'base' }),
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" autoFocus value={dName} disabled={savingInline} onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
      ) : (<span>{r.positionName ?? ''}</span>),
    },
    {
      field: 'sort', header: 'Sort', width: 110, align: 'right', type: 'number', sortable: false, value: (r) => r.sort ?? null,
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" type="number" value={dSort} disabled={savingInline} onClick={(e) => e.stopPropagation()} onChange={(e) => setDSort(e.target.value)} />
      ) : (<span>{r.sort ?? ''}</span>),
    },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Position</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-position" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Position" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        <AppToolbar
          onNew={openNew}
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onDelete={() => void removeSelected()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="Position" headers={['Position', 'Sort']} rows={visible.map((r) => [r.positionName ?? '', r.sort != null ? String(r.sort) : ''])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card" ref={cardRef}>
          <AppDataGrid<Position> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={inlineId ?? editing?.id ?? null}
            initialSort={{ key: 'positionName', dir: 'asc' }} loading={isFetching} emptyText="No positions found." />
        </div>
      </div>

      {editing !== undefined && (
        <div className="acc-overlay" onMouseDown={close}>
          <div className={`acc-dialog${maxed ? ' acc-dialog-max' : ''}`} style={maxStyle} onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>{editing ? `Position - ${editing.positionName ?? ''}` : 'Position'}</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={close} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">Position<span className="req">*</span></label>
                <div className="acc-fh-control">
                  <input className="acc-input" autoFocus value={name} onChange={(e) => { setName(e.target.value); if (err) setErr(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
                  {err && <div className="acc-err">{err}</div>}
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">Sort</label>
                <div className="acc-fh-control">
                  <input className="acc-input" type="number" value={sort} placeholder="(auto)" onChange={(e) => setSort(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void submit()} disabled={save.isPending}><span className="tb-i">💾</span> {save.isPending ? 'Saving…' : 'Save'}</button>
              <button className="ca-btn" onClick={close}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
